import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {
  Box,
  Stack,
  Typography,
  TextField,
  MenuItem,
  Select,
  OutlinedInput,
  InputLabel,
  FormControl,
  FormHelperText,
  Radio,
  Checkbox,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import LoadingScreen from "src/components/loading-screen";
import EventImageLayout from "src/layouts/event";
import { PrimaryLoadingButton } from "../listing-details/listingButtons";
import { useSnackbar } from "notistack";
import { CREATE_CART_ITEMS } from "src/graphql/mutations";
import { CREATE_BOOKING_HOLDING_FEE_USER } from "src/graphql/mutations/bookings";
import { useMutationWithAuth } from "src/graphql/instances";
import { ANSWER_QUESTIONNAIRE } from "src/graphql/mutations/questionnaire";

// keep in sync with backend QUESTIONNAIRE_TEXT_MAX_LENGTH
const TEXT_MAX_LENGTH = 500;

const OptionRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== "selected",
})(({ theme, selected }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  width: "100%",
  borderRadius: 16,
  padding: theme.spacing(1.25, 2),
  cursor: "pointer",
  userSelect: "none",
  background: "rgba(255,255,255,0.06)",
  backdropFilter: "blur(10px)",
  border: selected
    ? "1px solid rgba(255,255,255,0.9)"
    : "1px solid rgba(255,255,255,0.08)",
  transition: "background 0.15s ease, border-color 0.15s ease",
  "&:hover": {
    background: "rgba(255,255,255,0.1)",
  },
}));

const pillControlSx = {
  p: 0,
  pointerEvents: "none",
  color: "rgba(255,255,255,0.4)",
  "&.Mui-checked": {
    color: "#fff",
  },
};

export default function QuestionnairePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const { eventData, pendingCartItems, pendingBooking, checkoutUrl } = location.state || {};

  const [createCartItems] = useMutationWithAuth(CREATE_CART_ITEMS);
  const [createBookingHoldingFee] = useMutationWithAuth(CREATE_BOOKING_HOLDING_FEE_USER);
  const [answerQuestionnaire] = useMutationWithAuth(ANSWER_QUESTIONNAIRE);

  const questions = useMemo(
    () =>
      [...(eventData?.questionnaire?.questions ?? [])].sort(
        (a, b) => a.order - b.order
      ),
    [eventData]
  );

  const validationSchema = useMemo(() => {
    const shape = {};
    questions.forEach((q) => {
      const fieldName = `q_${q.id}`;
      if (q.type === "MULTIPLE_CHOICE") {
        shape[fieldName] = q.required
          ? Yup.array()
            .min(1, t("eventManager.questionnaire.required"))
            .required(t("eventManager.questionnaire.required"))
          : Yup.array();
      } else if (q.type === "DATE") {
        shape[fieldName] = q.required
          ? Yup.string().nullable().required(t("eventManager.questionnaire.required"))
          : Yup.string().nullable();
      } else {
        shape[fieldName] = q.required
          ? Yup.string().trim().required(t("eventManager.questionnaire.required"))
          : Yup.string();
      }
    });
    return Yup.object().shape(shape);
  }, [questions, t]);

  const defaultValues = useMemo(() => {
    const values = {};
    questions.forEach((q) => {
      values[`q_${q.id}`] =
        q.type === "MULTIPLE_CHOICE" ? [] : q.type === "DATE" ? null : "";
    });
    return values;
  }, [questions]);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  if (!eventData) {
    return <LoadingScreen />;
  }

  const onError = (formErrors) => {
    const firstInvalid = questions.find((q) => formErrors[`q_${q.id}`]);
    if (!firstInvalid) return;

    const el = document.getElementById(`question-${firstInvalid.id}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const onSubmit = async (formValues) => {
    const answers = questions.map((q) => ({
      questionId: q.id,
      value: formValues[`q_${q.id}`],
    }));

    try {
      const { data } = await createCartItems({ variables: { cartItems: pendingCartItems } });

      const createdCartItem = data?.createCartItems?.items?.find(
        (it) => it.ticketType?.id === pendingCartItems[0]?.id
      );
      if (!createdCartItem) throw new Error("Could not create cart item");

      await answerQuestionnaire({
        variables: { cartItemId: createdCartItem.id, answers },
      });

      if (pendingBooking) {
        await createBookingHoldingFee({ variables: { metadata: { booking: pendingBooking } } });
      }

      navigate(checkoutUrl);
    } catch (e) {
      console.log(e);
      enqueueSnackbar(t("eventManager.questionnaire.submitError"), { variant: "error" });
    }
  };

  return (
    <EventImageLayout
      title={`Questionnaire ${eventData?.general?.name}`}
      image={eventData?.general?.images[0]}
      fields={eventData?.general?.fields}
      useFullHeight={true}
    >
      <Box sx={{ maxWidth: 640, mx: "auto", px: 2, py: 4 }}>
        <Stack
          spacing={5}
          component="form"
          onSubmit={handleSubmit(onSubmit, onError)}
        >
          {questions.map((question, index) => (
            <QuestionField
              key={question.id}
              index={index}
              question={question}
              control={control}
              error={errors[`q_${question.id}`]}
              t={t}
            />
          ))}

          <PrimaryLoadingButton type="submit" size="large">
            {t("productPage.continue")}
          </PrimaryLoadingButton>
        </Stack>
      </Box>
    </EventImageLayout>
  );
}

function QuestionField({ index, question, control, error, t }) {
  const fieldName = `q_${question.id}`;

  return (
    <Box id={`question-${question.id}`}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {`${index + 1}. ${question.title}`}
        {question.required && (
          <Typography component="span" color="error">
            {" *"}
          </Typography>
        )}
      </Typography>
      {question.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {question.description}
        </Typography>
      )}

      {question.type === "TEXT" && (
        <Controller
          name={fieldName}
          control={control}
          render={({ field }) => {
            const value = field.value ?? "";
            return (
              <Box sx={{ mt: 1 }}>
                <TextField
                  {...field}
                  value={value}
                  fullWidth
                  multiline
                  minRows={3}
                  inputProps={{ maxLength: TEXT_MAX_LENGTH }}
                  error={!!error}
                  helperText={error?.message}
                />
                <Typography variant="caption" color="grey" sx={{ ml: 0.5 }}>
                  {`${value.length}/${TEXT_MAX_LENGTH} characters`}
                </Typography>
              </Box>
            );
          }}
        />
      )}

      {question.type === "DATE" && (
        <Controller
          name={fieldName}
          control={control}
          render={({ field }) => (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={field.value ? dayjs(field.value) : null}
                onChange={(date) =>
                  field.onChange(date ? date.format("YYYY-MM-DD") : null)
                }
                format="D/M/YYYY"
                sx={{ mt: 1, width: "100%" }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!error,
                    helperText: error?.message,
                  },
                }}
              />
            </LocalizationProvider>
          )}
        />
      )}

      {question.type === "DROPDOWN" && (
        <Controller
          name={fieldName}
          control={control}
          render={({ field }) => (
            <FormControl fullWidth error={!!error} sx={{ mt: 1 }}>
              <InputLabel sx={{ "&.Mui-focused": { color: "text.primary" } }}>
                {t("eventManager.questionnaire.selectOption")}
              </InputLabel>
              <Select
                {...field}
                input={<OutlinedInput label={t("eventManager.questionnaire.selectOption")} />}
                renderValue={(val) => val}
              >
                {question.options.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
              {error && <FormHelperText>{error.message}</FormHelperText>}
            </FormControl>
          )}
        />
      )}

      {question.type === "SINGLE_CHOICE" && (
        <Controller
          name={fieldName}
          control={control}
          render={({ field }) => (
            <Stack spacing={1.25} sx={{ mt: 1.5 }}>
              {question.options.map((opt) => {
                const isSelected = field.value === opt;
                return (
                  <OptionRow
                    key={opt}
                    selected={isSelected}
                    onClick={() => field.onChange(isSelected ? "" : opt)}
                  >
                    <Radio sx={pillControlSx} checked={isSelected} />
                    <Typography sx={{ fontSize: "0.95rem" }}>{opt}</Typography>
                  </OptionRow>
                );
              })}
              {error && (
                <FormHelperText error sx={{ ml: 1 }}>
                  {error.message}
                </FormHelperText>
              )}
            </Stack>
          )}
        />
      )}

      {question.type === "MULTIPLE_CHOICE" && (
        <Controller
          name={fieldName}
          control={control}
          render={({ field }) => {
            const selected = field.value ?? [];
            const toggle = (opt) => {
              field.onChange(
                selected.includes(opt)
                  ? selected.filter((v) => v !== opt)
                  : [...selected, opt]
              );
            };
            return (
              <Stack spacing={1.25} sx={{ mt: 1.5 }}>
                {question.options.map((opt) => {
                  const isSelected = selected.includes(opt);
                  return (
                    <OptionRow
                      key={opt}
                      selected={isSelected}
                      onClick={() => toggle(opt)}
                    >
                      <Checkbox sx={pillControlSx} checked={isSelected} />
                      <Typography sx={{ fontSize: "0.95rem" }}>{opt}</Typography>
                    </OptionRow>
                  );
                })}
                {error && (
                  <FormHelperText error sx={{ ml: 1 }}>
                    {error.message}
                  </FormHelperText>
                )}
              </Stack>
            );
          }}
        />
      )}
    </Box>
  );
}